import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from '../icons/AppIcon';
import { Input, Button, Toggle } from '../design-system';
import { RichTextEditor } from '../design-system/RichTextEditor';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { FlowCanvas } from './FlowCanvas';
import { NodeTypePicker } from './NodeTypePicker';
import { NodeConfigForm } from './NodeConfigForm';
import { FlowNode, FlowNodeType, FlowFormData, FlowRunHighlight, NODE_TYPE_META, emptyNode, getNodeEdges, recordNodeTypeUsage, genId, sanitizeNodeName } from './flow-types';
import { useAppStore } from '../../store/appStore';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export type { FlowFormData, FlowNode, FlowNodeType } from './flow-types';

const isWeb = Platform.OS === 'web';

export interface FlowFormProps {
  initialData?: Partial<FlowFormData> & { _id?: string };
  channel?: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
  onSubmit: (data: FlowFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  hideActions?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  onPreviewPaneChange?: (node: React.ReactNode) => void;
  onSidebarPaneChange?: (node: React.ReactNode) => void;
  /** Called when handleSubmit's local validation fails, BEFORE onSubmit would ever be
   * reached — validation errors otherwise only render inside whichever node's config panel
   * happens to be open, which is invisible whenever a different node (or no node) is
   * selected. This callback lets the host screen surface a toast so a failed save is never
   * silent. Receives a short human-readable summary of the first problem found. */
  onValidationError?: (message: string) => void;
  /** A run drawn over the canvas (opened from Flow runs). */
  runHighlight?: FlowRunHighlight | null;
}

const outcomeSummaryForNode = (node: FlowNode, nodesById: Map<string, FlowNode>): string => {
  const meta = NODE_TYPE_META.find((m) => m.type === node.type);
  return meta?.label || node.type;
};

const MAX_PREVIEW_NODES = 30;
interface PreviewStep {
  node: FlowNode;
  depth: number;
  edges: Array<{ label: string; targetLabel: string }>;
}
const buildFlowPreview = (nodes: FlowNode[], entryNodeId: string): PreviewStep[] => {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const steps: PreviewStep[] = [];
  const visited = new Set<string>();
  const queue: Array<{ id: string; depth: number }> = entryNodeId ? [{ id: entryNodeId, depth: 0 }] : [];

  while (queue.length > 0 && steps.length < MAX_PREVIEW_NODES) {
    const { id, depth } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const node = nodesById.get(id);
    if (!node) continue;

    const edges = getNodeEdges(node).map((e) => ({
      label: e.label,
      targetLabel: e.nextNodeId ? (nodesById.get(e.nextNodeId)?.name || 'unknown') : '(ends flow)',
    }));
    steps.push({ node, depth, edges });

    for (const edge of getNodeEdges(node)) {
      if (edge.nextNodeId && !visited.has(edge.nextNodeId)) queue.push({ id: edge.nextNodeId, depth: depth + 1 });
    }
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      const edges = getNodeEdges(node).map((e) => ({
        label: e.label,
        targetLabel: e.nextNodeId ? (nodesById.get(e.nextNodeId)?.name || 'unknown') : '(ends flow)',
      }));
      steps.push({ node, depth: -1, edges });
    }
  }

  return steps;
};

/** Validates one node's config against its type's required fields, using the same rules as
 * the backend's flow.service.ts validateFlow — kept in sync manually since this is a
 * client-side pre-check, not a substitute for server-side validation. */
/** Writes a nextNodeId into the right place for a given edgeKey (see flow-types.ts's
 * getNodeEdges: "default" -> config.nextNodeId or defaultNextNodeId for branch nodes,
 * "button:<id>" -> that button's nextNodeId, "group:<index>" -> that branch condition
 * group's nextNodeId, …) — the write side of drag-to-connect on the canvas. */
function linkEdge(n: FlowNode, edgeKey: string, targetNodeId: string | undefined): FlowNode {
  if (edgeKey === 'default') {
    if (n.type === 'branch') return { ...n, defaultNextNodeId: targetNodeId };
    if (n.type === 'check_language') return { ...n, config: { ...n.config, languageDefaultNextNodeId: targetNodeId } };
    return { ...n, config: { ...n.config, nextNodeId: targetNodeId } };
  }
  if (edgeKey === 'reply') {
    return n.type === 'wait' ? { ...n, config: { ...n.config, waitReplyNextNodeId: targetNodeId } } : n;
  }
  if (edgeKey === 'fallback') {
    if (n.type === 'ai_step' || n.type === 'ai_agent') return { ...n, config: { ...n.config, aiDefaultNextNodeId: targetNodeId } };
    if (n.type === 'check_time') return { ...n, config: { ...n.config, timeDefaultNextNodeId: targetNodeId } };
    return n;
  }
  if (edgeKey === 'notfound') return { ...n, config: { ...n.config, recordNotFoundNextNodeId: targetNodeId } };
  if (edgeKey === 'silence') return { ...n, config: { ...n.config, agentSilenceNextNodeId: targetNodeId } };
  if (edgeKey === 'maxturns') return { ...n, config: { ...n.config, agentMaxTurnsNextNodeId: targetNodeId } };
  if (edgeKey.startsWith('exit:')) {
    const id = edgeKey.slice('exit:'.length);
    return { ...n, config: { ...n.config, agentExits: (n.config.agentExits || []).map((e) => (e.id === id ? { ...e, nextNodeId: targetNodeId } : e)) } };
  }
  if (edgeKey.startsWith('lang:')) {
    const index = parseInt(edgeKey.slice('lang:'.length), 10);
    return { ...n, config: { ...n.config, languageRoutes: (n.config.languageRoutes || []).map((r, i) => (i === index ? { ...r, nextNodeId: targetNodeId } : r)) } };
  }
  if (edgeKey.startsWith('ai:')) {
    const id = edgeKey.slice('ai:'.length);
    return { ...n, config: { ...n.config, aiCategories: (n.config.aiCategories || []).map((c) => (c.id === id ? { ...c, nextNodeId: targetNodeId } : c)) } };
  }
  if (edgeKey.startsWith('time:')) {
    const id = edgeKey.slice('time:'.length);
    return { ...n, config: { ...n.config, timeRoutes: (n.config.timeRoutes || []).map((r) => (r.id === id ? { ...r, nextNodeId: targetNodeId } : r)) } };
  }
  if (edgeKey.startsWith('button:')) {
    const buttonId = edgeKey.slice('button:'.length);
    return {
      ...n,
      config: {
        ...n.config,
        buttons: (n.config.buttons || []).map((b) => (b.id === buttonId ? { ...b, nextNodeId: targetNodeId } : b)),
      },
    };
  }
  if (edgeKey.startsWith('group:')) {
    const groupIndex = parseInt(edgeKey.slice('group:'.length), 10);
    return {
      ...n,
      conditionGroups: (n.conditionGroups || []).map((g, i) => (i === groupIndex ? { ...g, nextNodeId: targetNodeId } : g)),
    };
  }
  return n;
}

/** Renames one of a node's connections where its name lives: the button label, group name,
 * AI category, time rule or language route name — or, for Next / Otherwise / fallback / reply,
 * the node's edgeLabels. An empty name clears an optional name; a button or category keeps its old one. */
function renameEdgeOn(n: FlowNode, edgeKey: string, text: string): FlowNode {
  const name = text.trim();
  const [kind, ref] = [edgeKey.split(':')[0], edgeKey.slice(edgeKey.indexOf(':') + 1)];
  if (kind === 'button') {
    if (!name) return n;
    return { ...n, config: { ...n.config, buttons: (n.config.buttons || []).map((b) => (b.id === ref ? { ...b, label: name.slice(0, 20) } : b)) } };
  }
  if (kind === 'ai') {
    if (!name) return n;
    return { ...n, config: { ...n.config, aiCategories: (n.config.aiCategories || []).map((c) => (c.id === ref ? { ...c, label: name.slice(0, 60) } : c)) } };
  }
  if (kind === 'exit') {
    if (!name) return n;
    return { ...n, config: { ...n.config, agentExits: (n.config.agentExits || []).map((e) => (e.id === ref ? { ...e, label: name.slice(0, 60) } : e)) } };
  }
  if (kind === 'time') return { ...n, config: { ...n.config, timeRoutes: (n.config.timeRoutes || []).map((r) => (r.id === ref ? { ...r, label: name.slice(0, 40) } : r)) } };
  if (kind === 'group') {
    const index = parseInt(ref, 10);
    return { ...n, conditionGroups: (n.conditionGroups || []).map((g, i) => (i === index ? { ...g, name: name.slice(0, 60) || undefined } : g)) };
  }
  if (kind === 'lang') {
    const index = parseInt(ref, 10);
    return { ...n, config: { ...n.config, languageRoutes: (n.config.languageRoutes || []).map((r, i) => (i === index ? { ...r, name: name.slice(0, 60) || undefined } : r)) } };
  }
  const edgeLabels = { ...(n.config.edgeLabels || {}) };
  if (name) edgeLabels[edgeKey] = name.slice(0, 60);
  else delete edgeLabels[edgeKey];
  return { ...n, config: { ...n.config, edgeLabels: Object.keys(edgeLabels).length ? edgeLabels : undefined } };
}

/** Removes nodes and every connection that pointed at them (a dangling connection would fail the save). */
function withoutNodes(nodes: FlowNode[], ids: Set<string>): FlowNode[] {
  return nodes
    .filter((n) => !ids.has(n.id))
    .map((n) => getNodeEdges(n).reduce((acc, edge) => (edge.nextNodeId && ids.has(edge.nextNodeId) ? linkEdge(acc, edge.key, undefined) : acc), n));
}

function validateNode(node: FlowNode, allNodeIds: Set<string>): Record<string, string> {
  const errors: Record<string, string> = {};
  const cfg = node.config || {};
  const requireNext = (required: boolean) => {
    if (required && !cfg.nextNodeId && !(cfg.buttons?.length)) errors.nextNodeId = 'Select what happens next';
  };
  // "contact" is reserved — flow-engine.service.ts seeds {{contact.*}} from the real contact
  // profile at the start of every run; a saveAs/saveResultAs literally named "contact" would
  // silently overwrite it for the rest of that run, breaking {{contact.*}} in every later node.
  if (cfg.saveAs?.trim().toLowerCase() === 'contact') errors.saveAs = '"contact" is reserved — pick another name';
  if (cfg.saveResultAs?.trim().toLowerCase() === 'contact') errors.saveResultAs = '"contact" is reserved — pick another name';
  switch (node.type) {
    case 'start':
      requireNext(true);
      break;
    case 'send_message':
    case 'ask_and_wait': {
      if (!cfg.text?.trim() && !cfg.mediaUrl) errors.text = 'Message text is required';
      if (node.type === 'ask_and_wait' && !cfg.saveAs?.trim()) errors.saveAs = 'Variable name is required';
      // Only go_to_node buttons pause the flow waiting for a tap — visit_website/call buttons
      // never produce a reply, so a node with only those still needs its own "Then" node.
      const hasResumableButtons = (cfg.buttons || []).some((b) => !b.actionType || b.actionType === 'go_to_node');
      (cfg.buttons || []).forEach((b, i) => {
        if (b.actionType === 'visit_website' && !b.url?.trim()) errors[`button_${i}_url`] = `Button "${b.label || i + 1}" needs a URL`;
        if (b.actionType === 'call' && !b.phoneNumber?.trim()) errors[`button_${i}_phone`] = `Button "${b.label || i + 1}" needs a phone number`;
      });
      // Per-type caps mirror WhatsApp's real limits — see flow-engine.service.ts's send_message
      // case and flow.service.ts's matching backend validation.
      const goToNodeButtonCount = (cfg.buttons || []).filter((b) => !b.actionType || b.actionType === 'go_to_node').length;
      const visitWebsiteButtonCount = (cfg.buttons || []).filter((b) => b.actionType === 'visit_website').length;
      const callButtonCount = (cfg.buttons || []).filter((b) => b.actionType === 'call').length;
      if (goToNodeButtonCount > 10) errors.buttons = 'Too many "go to checker" buttons (max 10)';
      else if (visitWebsiteButtonCount > 2) errors.buttons = 'Too many "visit website" buttons (max 2)';
      else if (callButtonCount > 1) errors.buttons = 'Only one "call" button is allowed';
      // Beyond 3 go_to_node buttons, flow-engine sends a WhatsApp LIST message instead of the
      // raw 3-button type — that message needs an explicit label for the button that opens it.
      if (goToNodeButtonCount > 3 && !cfg.listButtonText?.trim()) {
        errors.listButtonText = 'Enter the text for the button that opens the list';
      }
      requireNext(!hasResumableButtons);
      break;
    }
    case 'webhook':
      if (!cfg.webhookId) errors.webhookId = 'Select a webhook';
      requireNext(true);
      break;
    case 'update_contact':
      if (cfg.contactUpdateMode === 'custom') {
        if (!cfg.customFieldListId) errors.customFieldListId = 'Select a contacts data list';
        else if (!(cfg.customFieldValues || []).some((v) => v.columnId && v.valueTemplate?.trim())) errors.customFieldValues = 'Add at least one field and value';
      } else {
        if (!cfg.contactField?.trim()) errors.contactField = 'Contact field is required';
        if (!cfg.valueTemplate?.trim()) errors.valueTemplate = 'Value is required';
      }
      requireNext(true);
      break;
    case 'assign_user':
      if (!cfg.userIds?.length) errors.userIds = 'Select at least one user';
      requireNext(true);
      break;
    case 'assign_department':
      if (!cfg.departmentIds?.length) errors.departmentIds = 'Select at least one department';
      requireNext(true);
      break;
    case 'send_dataset':
      if (!cfg.setId) errors.setId = 'Select a dataset';
      requireNext(true);
      break;
    case 'show_product':
      if (!cfg.productId && !cfg.productRef?.trim()) errors.productId = 'Select a product, or take it from the conversation';
      requireNext(true);
      break;
    case 'show_catalog':
      if (!cfg.catalogId && !cfg.catalogRef?.trim()) errors.catalogId = 'Select a catalog, or take it from the conversation';
      requireNext(true);
      break;
    case 'open_url':
      if (!cfg.url?.trim()) errors.url = 'URL is required';
      requireNext(true);
      break;
    case 'create_task':
      if (!cfg.taskTitle?.trim()) errors.taskTitle = 'Task title is required';
      if (!cfg.userIds?.length && !cfg.departmentIds?.length) errors.userIds = 'Select at least one user or department';
      requireNext(true);
      break;
    case 'find_contact_tasks':
    case 'find_contact_events':
      requireNext(true);
      break;
    case 'create_calendar_event':
      if (!cfg.calendarId) errors.calendarId = 'Select a calendar';
      if (!cfg.eventTitle?.trim()) errors.eventTitle = 'Event title is required';
      // Fixed-time mode needs BOTH dates — one without the other is inconsistent (not valid
      // slot-picking mode either, which needs neither set).
      if (cfg.eventStartDate && !cfg.eventEndDate) errors.eventEndDate = 'End date is required when a start date is set';
      if (!cfg.eventStartDate && cfg.eventEndDate) errors.eventStartDate = 'Start date is required when an end date is set';
      requireNext(true);
      break;
    case 'write_data_list': {
      const op = cfg.dataListOperation;
      if (!cfg.dataListId) errors.dataListId = 'Select a data list';
      if (!op) errors.dataListOperation = 'Select an operation';
      const validMappings = (cfg.dataListValueMappings || []).filter((m) => m.columnId && m.valueTemplate?.trim());
      const validCriteria = (cfg.dataListMatchCriteria || []).filter((c) => c.columnId && c.valueTemplate?.trim());
      if ((op === 'create' || op === 'update') && validMappings.length === 0) {
        errors.dataListValueMappings = 'Add at least one value mapping';
      }
      if ((op === 'update' || op === 'find' || op === 'delete') && validCriteria.length === 0) {
        errors.dataListMatchCriteria = 'Add at least one match criterion';
      }
      requireNext(true);
      break;
    }
    case 'branch': {
      if (!node.conditionGroups?.length) errors.conditionGroups = 'Add at least one condition group';
      // An empty value would match everyone ("contains nothing" is always true).
      const empty = (v: any) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.every((x) => x === '' || x == null));
      (node.conditionGroups || []).forEach((group, gi) => group.conditions.forEach((c, ci) => {
        if (!['has_value', 'is_empty'].includes(c.operator) && c.field !== 'formula' && empty(c.value)) {
          errors[`group.${gi}.condition.${ci}`] = 'Enter a value';
          errors.conditionGroups = errors.conditionGroups || `${group.name || `Group ${gi + 1}`}: a condition has no value`;
        }
      }));
      break;
    }
    case 'check_language': {
      if (!cfg.languageInputTemplate?.trim()) {
        errors.languageInputTemplate = 'Add a message/variable to detect the language of';
      }
      const routes = cfg.languageRoutes || [];
      if (routes.some((r) => !r.languages || r.languages.length === 0)) {
        errors.languageRoutes = 'Every route needs at least one language selected';
      }
      break;
    }
    case 'generate_text':
      if (!cfg.saveResultAs?.trim()) errors.saveResultAs = 'Variable name is required';
      if (cfg.generateLength !== undefined && (cfg.generateLength < 1 || cfg.generateLength > 64)) {
        errors.generateLength = 'Length must be between 1 and 64';
      }
      requireNext(true);
      break;
    case 'send_otp':
      if (!cfg.otpCodeTemplate?.trim()) errors.otpCodeTemplate = 'Code value is required';
      if ((cfg.otpMode || 'template') === 'template') {
        if (!cfg.otpTemplateId) errors.otpTemplateId = 'Select an approved template';
      } else {
        if (!cfg.otpBodyTemplate?.trim()) errors.otpBodyTemplate = 'Message body is required';
        if (!cfg.otpButtonText?.trim()) errors.otpButtonText = 'Button text is required';
      }
      requireNext(true);
      break;
    case 'pause':
      break;
    case 'ai_step': {
      if (!cfg.agentActAs) errors.agentActAs = 'Choose the AI agent this step speaks as';
      const mode = cfg.aiMode || 'classify';
      if (mode === 'classify') {
        const categories = cfg.aiCategories || [];
        if (categories.length < 2) errors.aiCategories = 'Add at least two categories';
        else if (categories.some((c) => !c.label?.trim())) errors.aiCategories = 'Every category needs a name';
      } else if (mode === 'extract') {
        if (!(cfg.aiFields || []).some((f) => f.name?.trim())) errors.aiFields = 'Add at least one field';
        if (!cfg.saveResultAs?.trim()) errors.saveResultAs = 'Variable name is required';
        requireNext(true);
      } else {
        if (!cfg.aiSendReply && !cfg.saveResultAs?.trim()) errors.saveResultAs = 'Send the reply or save it to a variable';
        requireNext(true);
      }
      break;
    }
    case 'check_time': {
      const routes = cfg.timeRoutes || [];
      const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
      if (routes.length === 0) errors.timeRoutes = 'Add at least one rule';
      routes.forEach((r, i) => {
        const name = r.label || `Rule ${i + 1}`;
        const hasCriteria = r.businessHours || r.daysOfWeek?.length || r.daysOfMonth?.length || r.months?.length || r.timeFrom || r.dateFrom || r.dateTo;
        if (!hasCriteria) errors[`time_${i}`] = `${name} has no condition`;
        else if (!!r.timeFrom !== !!r.timeTo) errors[`time_${i}`] = `${name} needs both a start and an end time`;
        else if ((r.timeFrom && !hhmm.test(r.timeFrom)) || (r.timeTo && !hhmm.test(r.timeTo))) errors[`time_${i}`] = `${name}: use HH:mm, e.g. 09:00`;
      });
      break;
    }
    case 'wait': {
      const mode = cfg.waitMode || 'duration';
      if (mode === 'duration') {
        const amount = Number(cfg.waitAmount);
        const minutes = amount * ({ minutes: 1, hours: 60, days: 1440 }[cfg.waitUnit || 'minutes']);
        if (!Number.isFinite(amount) || amount < 1) errors.waitAmount = 'Enter how long to wait';
        else if (minutes > 30 * 1440) errors.waitAmount = 'The longest wait is 30 days';
      } else if (mode === 'until_time' && !/^([01]\d|2[0-3]):[0-5]\d$/.test(cfg.waitTime || '')) {
        errors.waitTime = 'Use HH:mm, e.g. 09:00';
      }
      requireNext(true);
      break;
    }
    case 'find_leads':
      if (!cfg.leadBrief?.trim()) errors.leadBrief = 'Describe who to find';
      break;
    case 'manage_assignments': {
      const op = cfg.asgOperation || 'find';
      if (op === 'assign' && !cfg.assignHandoff && !cfg.userIds?.length && !cfg.departmentIds?.length) errors.userIds = 'Choose who to assign to';
      if (op === 'assign' && cfg.asgAssignKind && cfg.asgAssignKind !== 'contact' && !cfg.asgAssignTargetIds?.trim()) errors.asgAssignTargetIds = 'Where do the ids come from?';
      if (op === 'get' && !cfg.asgId?.trim()) errors.asgId = 'Which assignment (id)?';
      if (op !== 'assign' && op !== 'get' && cfg.asgScope === 'ids' && !cfg.asgIds?.trim()) errors.asgIds = 'Where do the assignment ids come from?';
      if (op === 'transfer' && !cfg.asgTransferUserId && !cfg.asgTransferDepartmentId) errors.asgTransfer = 'Choose who to transfer to';
      if (op === 'update' && !cfg.asgSetPriority && !cfg.asgSetDueInHours && !cfg.asgSetDueDate && !cfg.asgSetTitle && !cfg.asgSetDescription && !cfg.asgSetStatus && !cfg.asgAddTags) errors.asgUpdate = 'Choose what to change';
      break;
    }
    case 'send_bulk_whatsapp': {
      if (!cfg.waTemplateName) errors.waTemplateId = 'Select an approved template';
      const source = cfg.bulkSource || 'contacts';
      if (source === 'data_list') {
        if (!cfg.bulkDataListId) errors.bulkDataListId = 'Select a data list';
        else if (!cfg.bulkPhoneColumnId) errors.bulkPhoneColumnId = 'Choose the column that holds the phone';
      }
      if (source === 'context' && !cfg.bulkContextPath?.trim()) errors.bulkContextPath = 'Choose the variable that holds the list';
      if (source === 'contacts' && !cfg.bulkTags?.length && !cfg.bulkContactListId && !cfg.bulkAssignedUserId && !cfg.bulkAssignedDepartmentId && !cfg.bulkCity && !cfg.bulkCreatedWithinDays) {
        errors.bulkSource = 'Add at least one filter — otherwise it messages every contact';
      }
      break;
    }
    case 'ai_agent': {
      if (!cfg.agentActAs) errors.agentActAs = 'Choose the AI agent this step speaks as';
      const exits = cfg.agentExits || [];
      if (!exits.length) errors.agentExits = 'Add at least one exit — how the AI hands the conversation on';
      exits.forEach((e, i) => {
        if (!e.label?.trim()) errors[`exit_${i}`] = 'Name this exit';
        else if (e.kind === 'custom' && !e.description?.trim()) errors[`exit_${i}`] = 'Describe when the AI should take this exit';
        else if (!e.nextNodeId) errors[`exit_${i}`] = 'Choose where this exit goes';
      });
      const builtIn = exits.filter((e) => e.kind !== 'custom').map((e) => e.kind);
      if (new Set(builtIn).size !== builtIn.length) errors.agentExits = 'Each built-in exit can be used once';
      if (cfg.agentSilenceMinutes && !cfg.agentSilenceNextNodeId) errors.agentSilence = 'Choose where to go when the customer goes silent';
      break;
    }
    case 'create_order':
    case 'create_quotation':
    case 'add_to_vendor': {
      const op = cfg.recordOperation || 'create';
      if (op === 'create' && node.type !== 'add_to_vendor' && !cfg.recordSource?.trim()) errors.recordSource = 'Which cart to use (e.g. agent.cart)';
      if (!['create', 'auto'].includes(op) && !(cfg.recordOperations?.length || 0) && cfg.recordTarget === 'number' && !cfg.recordNumber?.trim()) errors.recordNumber = 'Enter the number to look up';
      if (op === 'update' && node.type === 'create_order' && !cfg.recordStatus && !cfg.recordPaymentStatus && !cfg.recordNotes?.trim()) errors.recordStatus = 'Choose a status, payment or note';
      if (op === 'update' && node.type === 'create_quotation' && !cfg.recordStatus && !cfg.quotationValidDays && !cfg.recordNotes?.trim()) errors.recordStatus = 'Choose a status, validity or note';
      break;
    }
    case 'create_contact': {
      const f = cfg.recordFields || {};
      if (!f.name?.trim()) errors.recordName = 'Name is required';
      if (!f.phone?.trim() && !f.email?.trim()) errors.recordPhone = 'A phone or email is required';
      break;
    }
    case 'add_to_list':
    case 'remove_from_list':
      if (!cfg.dataListId) errors.dataListId = 'Select a list';
      break;
    case 'send_template':
      if (!cfg.waTemplateName) errors.waTemplateId = 'Select an approved template';
      break;
    case 'end':
      break;
  }
  return errors;
}

export const FlowForm: React.FC<FlowFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save Flow',
  loading = false,
  hideActions = false,
  submitRef,
  onPreviewPaneChange,
  onSidebarPaneChange,
  onValidationError,
  runHighlight,
}) => {
  const styles = useThemedStyles(getStyles);
  const initialNodes = initialData?.nodes?.length
    ? initialData.nodes
    : (() => {
        const welcome = emptyNode('send_message', 'Welcome');
        const start = { ...emptyNode('start', 'Start'), config: { nextNodeId: welcome.id } };
        return [start, welcome];
      })();
  const initialEntryNodeId = initialData?.entryNodeId || initialNodes.find((n) => n.type === 'start')?.id || initialNodes[0].id;
  const [formData, setFormData] = useState<FlowFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    nodes: initialNodes,
    entryNodeId: initialEntryNodeId,
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
    sessionTimeoutMinutes: initialData?.sessionTimeoutMinutes ?? null,
    stopOnAgentReply: initialData?.stopOnAgentReply === true,
  });
  const [nodeErrors, setNodeErrors] = useState<Record<string, Record<string, string>>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(isWeb ? initialEntryNodeId : null);
  const setRightPaneCollapsed = useAppStore((s) => s.setRightPaneCollapsed);
  // rightPaneCollapsed is a global, cross-screen, persisted flag (e.g. toggled from the
  // contacts screen) — selecting a node here must force it open so the settings panel it just
  // populated is actually visible, not silently collapsed from an unrelated prior screen.
  const selectNode = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    if (nodeId) setRightPaneCollapsed(false);
  }, [setRightPaneCollapsed]);
  // Multi-select (shift-click / rubber-band drag on the canvas), separate from selectedNodeId
  // above which still drives the inspector panel. Drives the bulk-action toolbar (duplicate/
  // delete) and Ctrl+C/Ctrl+V below.
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  // In-memory clipboard for Ctrl+C/Ctrl+V — just the copied node ids, not a data snapshot;
  // Ctrl+V re-reads their current form via duplicateNodes at paste time (see below).
  const [clipboardNodeIds, setClipboardNodeIds] = useState<string[]>([]);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set([initialNodes[0].id]));

  // Undo/redo history for canvas edits. Capped at MAX_HISTORY entries so a long editing
  // session doesn't grow this unboundedly — formData is small/flat (see FlowFormData) so
  // whole-object snapshots are cheap even at that cap.
  const MAX_HISTORY = 50;
  const [history, setHistory] = useState<{ past: FlowFormData[]; future: FlowFormData[] }>({ past: [], future: [] });
  // While a multi-node group drag is in flight, individual onNodeDragStop calls (one per
  // dragged node) should NOT each push their own history entry — onSelectionDragStop pushes
  // one entry for the whole group instead. This ref is the suppression flag for that window.
  const suppressHistoryRef = useRef(false);

  /** Applies `updater` to formData and pushes the PRE-mutation state onto the undo stack
   * (unless suppressHistoryRef is set, e.g. mid-group-drag). Every mutation helper below
   * (updateNode/addNode/removeNode/setEdgeTarget/etc.) should go through this instead of
   * calling setFormData directly, so undo/redo stays in sync with every edit path. */
  const mutateFormData = useCallback((updater: (prev: FlowFormData) => FlowFormData) => {
    setFormData((prev) => {
      const next = updater(prev);
      if (next === prev) return prev; // no-op edits shouldn't create a history entry
      if (!suppressHistoryRef.current) {
        setHistory((h) => ({ past: [...h.past, prev].slice(-MAX_HISTORY), future: [] }));
      }
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    setFormData((current) => {
      let restored = current;
      setHistory((h) => {
        if (h.past.length === 0) return h;
        restored = h.past[h.past.length - 1];
        return { past: h.past.slice(0, -1), future: [current, ...h.future].slice(0, MAX_HISTORY) };
      });
      return restored;
    });
  }, []);

  const redo = useCallback(() => {
    setFormData((current) => {
      let restored = current;
      setHistory((h) => {
        if (h.future.length === 0) return h;
        restored = h.future[0];
        return { past: [...h.past, current].slice(-MAX_HISTORY), future: h.future.slice(1) };
      });
      return restored;
    });
  }, []);

  const selectedNode = formData.nodes.find((n) => n.id === selectedNodeId) || null;

  const validate = (): boolean => {
    const allNodeIds = new Set(formData.nodes.map((n) => n.id));
    const errorsByNode: Record<string, Record<string, string>> = {};
    let hasError = false;
    for (const node of formData.nodes) {
      const errs = validateNode(node, allNodeIds);
      if (Object.keys(errs).length > 0) {
        errorsByNode[node.id] = errs;
        hasError = true;
      }
    }
    const newFormErrors: Record<string, string> = {};
    if (!formData.name.trim()) newFormErrors.name = 'Flow name is required';
    const startNodes = formData.nodes.filter((n) => n.type === 'start');
    if (startNodes.length !== 1) {
      newFormErrors.entryNodeId = 'A flow needs exactly one Start node';
    } else if (formData.entryNodeId !== startNodes[0].id) {
      newFormErrors.entryNodeId = 'entryNodeId must be the Start node';
    }

    // Every node automatically exposes {{<sanitizedName>.input}} at runtime — two nodes
    // sanitizing down to the same key (or to nothing) would collide or be unreferenceable.
    const seenSanitizedNames = new Map<string, string>(); // sanitized -> first node's display name
    for (const node of formData.nodes) {
      const sanitized = sanitizeNodeName(node.name);
      if (!sanitized) {
        newFormErrors.duplicateNodeNames = `"${node.name || 'Untitled checker'}" needs a name containing at least one letter, number, or underscore.`;
      } else if (seenSanitizedNames.has(sanitized) && seenSanitizedNames.get(sanitized) !== node.name) {
        newFormErrors.duplicateNodeNames = `"${seenSanitizedNames.get(sanitized)}" and "${node.name}" resolve to the same checker name — please make them distinct.`;
      } else {
        seenSanitizedNames.set(sanitized, node.name);
      }
    }

    setNodeErrors(errorsByNode);
    setFormErrors(newFormErrors);

    const isValid = !hasError && Object.keys(newFormErrors).length === 0;
    if (!isValid && onValidationError) {
      // Validation errors otherwise only render inside whichever node's panel happens to be
      // open — jump to the first problem so the user isn't left staring at a "nothing
      // happened" save button, and surface a toast summarizing it.
      if (Object.keys(newFormErrors).length > 0) {
        onValidationError(newFormErrors.name || newFormErrors.entryNodeId || newFormErrors.duplicateNodeNames || 'Please fix the highlighted fields before saving.');
        setSelectedNodeId(null);
      } else {
        const firstErroredNodeId = Object.keys(errorsByNode)[0];
        const firstError = Object.values(errorsByNode[firstErroredNodeId] || {})[0];
        const node = formData.nodes.find((n) => n.id === firstErroredNodeId);
        setSelectedNodeId(firstErroredNodeId);
        setExpandedNodeIds((prev) => new Set(prev).add(firstErroredNodeId));
        onValidationError(`"${node?.name || 'A checker'}": ${firstError || 'has a missing field'}`);
      }
    }
    return isValid;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit(formData);
  };

  React.useEffect(() => {
    if (submitRef) submitRef.current = handleSubmit;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateNode = (nodeId: string, patch: Partial<FlowNode>) => {
    mutateFormData((prev) => ({ ...prev, nodes: prev.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)) }));
  };

  // Canvas positions live on each node (position field, persisted with the flow) rather than
  // separate local-only state, so the layout is exactly as the user left it the next time
  // this flow is opened instead of resetting to an auto-grid every load.
  const nodePositions = React.useMemo(() => {
    const map: Record<string, { x: number; y: number }> = {};
    for (const n of formData.nodes) {
      if (n.position) map[n.id] = n.position;
    }
    return map;
  }, [formData.nodes]);
  const handleMoveNode = (nodeId: string, position: { x: number; y: number }) => updateNode(nodeId, { position });

  /** Commits a whole group-drag (2+ nodes moved together) as ONE undo step instead of one per
   * node — see FlowCanvas.web.tsx's handleSelectionDragStop, which is the only caller. */
  const handleMoveNodes = (moves: { nodeId: string; position: { x: number; y: number } }[]) => {
    const byId = new Map(moves.map((m) => [m.nodeId, m.position]));
    mutateFormData((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) => (byId.has(n.id) ? { ...n, position: byId.get(n.id) } : n)),
    }));
  };

  // The canvas reports the centre of what is on screen (flow coordinates), so a new checker
  // lands where the user is looking instead of at a grid slot they may have scrolled away from.
  const viewCenterRef = useRef<(() => { x: number; y: number }) | null>(null);
  // Set while the type picker was opened from a selected node's open connection — the new
  // checker is wired to it.
  const [pendingLink, setPendingLink] = useState<{ sourceNodeId: string; edgeKey: string } | null>(null);

  /** Where a new checker goes: just below the connection it is added from, or in the middle of
   * the visible canvas; nudged off any checker already there. */
  const placeNewNode = (nodes: FlowNode[], link: { sourceNodeId: string; edgeKey: string } | null) => {
    let point: { x: number; y: number };
    const source = link ? nodes.find((n) => n.id === link.sourceNodeId) : undefined;
    if (source) {
      const edges = getNodeEdges(source);
      const index = Math.max(0, edges.findIndex((e) => e.key === link!.edgeKey));
      const base = source.type === 'start' ? { x: 0, y: 0 } : source.position || { x: 0, y: 0 };
      point = { x: base.x + (index - (edges.length - 1) / 2) * 150, y: base.y + 140 };
    } else {
      const center = viewCenterRef.current?.() || { x: 0, y: 0 };
      point = { x: center.x - 60, y: center.y - 40 };
    }
    const taken = (p: { x: number; y: number }) => nodes.some((n) => n.position && Math.abs(n.position.x - p.x) < 110 && Math.abs(n.position.y - p.y) < 90);
    for (let tries = 0; tries < 12 && taken(point); tries++) point = { x: point.x + 40, y: point.y + 50 };
    return { x: Math.round(point.x), y: Math.round(point.y) };
  };

  const addNode = (type: FlowNodeType, link: { sourceNodeId: string; edgeKey: string } | null = pendingLink) => {
    const node: FlowNode = { ...emptyNode(type, NODE_TYPE_META.find((m) => m.type === type)?.label || 'New checker'), position: placeNewNode(formData.nodes, link) };
    // Adding and wiring is one step, so a single undo removes both.
    mutateFormData((prev) => ({
      ...prev,
      nodes: [...prev.nodes.map((n) => (link && n.id === link.sourceNodeId ? linkEdge(n, link.edgeKey, node.id) : n)), node],
    }));
    selectNode(node.id);
    setExpandedNodeIds((prev) => new Set(prev).add(node.id));
    setTypePickerOpen(false);
    setPendingLink(null);
    recordNodeTypeUsage(type); // fire-and-forget — must never block/fail node creation
  };

  /** From the selected node's suggestions: a type adds it right away; no type opens the picker. */
  const addNodeFrom = (sourceNodeId: string, edgeKey: string, type?: FlowNodeType) => {
    if (type) {
      addNode(type, { sourceNodeId, edgeKey });
      return;
    }
    setPendingLink({ sourceNodeId, edgeKey });
    setTypePickerOpen(true);
  };

  const removeNode = (nodeId: string) => {
    const target = formData.nodes.find((n) => n.id === nodeId);
    if (target?.type === 'start') return; // Start is fixed — every flow needs exactly one
    mutateFormData((prev) => ({
      ...prev,
      nodes: withoutNodes(prev.nodes, new Set([nodeId])),
    }));
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
    setSelectedNodeIds((prev) => prev.filter((id) => id !== nodeId));
  };

  /** Bulk-delete version of removeNode — filters out ALL given ids in a single mutation, so
   * it produces one undoable history entry instead of one per node (which sequential
   * removeNode calls would do, since each is its own mutateFormData commit). */
  const removeNodes = (nodeIds: string[]) => {
    // Start is fixed — never removable, even via bulk/multi-select delete.
    const idSet = new Set(nodeIds.filter((id) => formData.nodes.find((n) => n.id === id)?.type !== 'start'));
    mutateFormData((prev) => ({ ...prev, nodes: withoutNodes(prev.nodes, idSet) }));
    if (selectedNodeId && idSet.has(selectedNodeId)) setSelectedNodeId(null);
    setSelectedNodeIds((prev) => prev.filter((id) => !idSet.has(id)));
  };

  /** Duplicates the given nodes: fresh node id + fresh nested button ids (genId, same
   * generator emptyNode/addNode already use), position offset so copies don't stack exactly
   * on the originals, and no outgoing connections (nextNodeId/defaultNextNodeId/group
   * nextNodeId all stripped) — a duplicate starts disconnected rather than silently sharing
   * the original's wiring. Single mutation, single history entry for the whole batch. */
  const duplicateNodes = (nodeIds: string[]) => {
    // Start is fixed — a flow can only have one, so it's excluded from copy/duplicate too.
    const idSet = new Set(nodeIds);
    const sourceNodes = formData.nodes.filter((n) => idSet.has(n.id) && n.type !== 'start');
    if (sourceNodes.length === 0) return;

    const copies: FlowNode[] = sourceNodes.map((n) => {
      const copy: FlowNode = JSON.parse(JSON.stringify(n));
      copy.id = genId('node');
      copy.name = `${n.name} copy`;
      if (copy.position) copy.position = { x: copy.position.x + 30, y: copy.position.y + 30 };
      copy.config = { ...copy.config, nextNodeId: undefined };
      if (copy.config.buttons) {
        copy.config.buttons = copy.config.buttons.map((b) => ({ ...b, id: genId('btn'), nextNodeId: undefined }));
      }
      copy.defaultNextNodeId = undefined;
      if (copy.conditionGroups) {
        copy.conditionGroups = copy.conditionGroups.map((g) => ({ ...g, nextNodeId: undefined }));
      }
      return copy;
    });

    mutateFormData((prev) => ({ ...prev, nodes: [...prev.nodes, ...copies] }));
    setSelectedNodeIds(copies.map((c) => c.id));
    setSelectedNodeId(copies.length === 1 ? copies[0].id : null);
  };

  // Ctrl/Cmd+Z undo, Ctrl+Y or Ctrl/Cmd+Shift+Z redo, Ctrl/Cmd+C copy, Ctrl/Cmd+V paste — but
  // only when focus isn't inside a text field, so node-name/message-text editing keeps using
  // the browser's own native undo/copy/paste instead of this canvas-level handling stealing
  // the keystroke. Copy/paste reuses duplicateNodes (see above) — copy just remembers which
  // ids were selected, paste re-runs duplicateNodes against them, so "paste" and "duplicate"
  // are the same underlying operation triggered two different ways.
  useEffect(() => {
    if (!isWeb) return;
    const isTextEntryFocused = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || isTextEntryFocused()) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      } else if (key === 'c') {
        const ids = selectedNodeIds.length > 0 ? selectedNodeIds : selectedNodeId ? [selectedNodeId] : [];
        if (ids.length > 0) {
          e.preventDefault();
          setClipboardNodeIds(ids);
        }
      } else if (key === 'v') {
        if (clipboardNodeIds.length > 0) {
          e.preventDefault();
          duplicateNodes(clipboardNodeIds);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, selectedNodeIds, selectedNodeId, clipboardNodeIds, duplicateNodes]);

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const setEdgeTarget = (sourceNodeId: string, edgeKey: string, targetNodeId: string | undefined) => {
    mutateFormData((prev) => ({ ...prev, nodes: prev.nodes.map((n) => (n.id === sourceNodeId ? linkEdge(n, edgeKey, targetNodeId) : n)) }));
  };

  const handleConnectEdge = (sourceNodeId: string, edgeKey: string, targetNodeId: string) => setEdgeTarget(sourceNodeId, edgeKey, targetNodeId);
  const handleRenameEdge = (sourceNodeId: string, edgeKey: string, text: string) =>
    mutateFormData((prev) => ({ ...prev, nodes: prev.nodes.map((n) => (n.id === sourceNodeId ? renameEdgeOn(n, edgeKey, text) : n)) }));
  /** Canvas Delete / Backspace (after its confirmation): steps and lines together, one undo step. */
  const handleDeleteItems = (nodeIds: string[], edges: Array<{ sourceNodeId: string; edgeKey: string }>) => {
    const ids = new Set(nodeIds.filter((id) => formData.nodes.find((n) => n.id === id)?.type !== 'start'));
    mutateFormData((prev) => {
      let nodes = prev.nodes;
      for (const edge of edges) nodes = nodes.map((n) => (n.id === edge.sourceNodeId ? linkEdge(n, edge.edgeKey, undefined) : n));
      return { ...prev, nodes: withoutNodes(nodes, ids) };
    });
    if (selectedNodeId && ids.has(selectedNodeId)) setSelectedNodeId(null);
    setSelectedNodeIds((prev) => prev.filter((id) => !ids.has(id)));
  };
  const handleDeleteEdge = (sourceNodeId: string, edgeKey: string) => setEdgeTarget(sourceNodeId, edgeKey, undefined);

  // What the selected checker still needs, live (not only after Save), for the canvas's
  // suggestions panel; missing connections are offered there separately.
  const selectedNodeIssues = React.useMemo(() => {
    if (!selectedNode) return [];
    const errs = validateNode(selectedNode, new Set(formData.nodes.map((n) => n.id)));
    return Object.entries(errs).filter(([key]) => key !== 'nextNodeId').map(([, message]) => message);
  }, [selectedNode, formData.nodes]);

  const flowPreview = React.useMemo(() => buildFlowPreview(formData.nodes, formData.entryNodeId), [formData.nodes, formData.entryNodeId]);

  const renderFlowSettings = () => (
    <>
      <View style={styles.field}>
        <Input label="Flow Name *" value={formData.name} onChangeText={(text) => setFormData((prev) => ({ ...prev, name: text }))} placeholder="e.g. Age verification" error={formErrors.name} />
      </View>
      <View style={styles.field}>
        <RichTextEditor label="Description (Optional)" value={formData.description || ''} onChange={(text) => setFormData((prev) => ({ ...prev, description: text }))} placeholder="What does this flow do?" maxLength={500} showToolbar={false} />
      </View>
      <View style={styles.field}>
        <Toggle label="Active" description="Inactive flows are skipped wherever attached" value={formData.isActive} onValueChange={(value) => setFormData((prev) => ({ ...prev, isActive: value }))} />
      </View>
      <View style={styles.field}>
        <Toggle
          label="Stop when a team member replies"
          description="If someone from your team answers in the conversation, this flow stops for that contact so you don't talk over each other"
          value={formData.stopOnAgentReply === true}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, stopOnAgentReply: value }))}
        />
      </View>
      <View style={styles.field}>
        <Input
          label="End the flow after the contact is silent for (minutes)"
          value={formData.sessionTimeoutMinutes ? String(formData.sessionTimeoutMinutes) : ''}
          onChangeText={(text) => {
            const minutes = parseInt(text.replace(/[^0-9]/g, ''), 10);
            setFormData((prev) => ({ ...prev, sessionTimeoutMinutes: Number.isFinite(minutes) && minutes > 0 ? Math.min(minutes, 43200) : null }));
          }}
          keyboardType="number-pad"
          placeholder="Default (set by your administrator)"
          error={formErrors.sessionTimeoutMinutes}
        />
        <Text style={styles.helperText}>After this, their next message starts fresh instead of answering an old question. Leave empty for the default.</Text>
      </View>
      {formErrors.duplicateNodeNames && <Text style={styles.errorText}>{formErrors.duplicateNodeNames}</Text>}
      <Text style={styles.helperText}>Every flow begins at its Start node — drag a connection from Start on the canvas to pick what runs first. Tap a checker on the canvas to edit it. This panel shows flow-level settings when nothing is selected.</Text>
    </>
  );

  const renderNodeEditor = (node: FlowNode) => (
    <>
      <View style={styles.canvasPanelHeader}>
        {isWeb && (
          <TouchableOpacity onPress={() => setSelectedNodeId(null)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="close" size={20} color={colors.textDark} />
          </TouchableOpacity>
        )}
        <Text style={[styles.nodeTitle, styles.canvasPanelHeaderTitle]} numberOfLines={1}>{node.name || 'Untitled checker'}</Text>
        {formData.nodes.length > 1 && (
          <TouchableOpacity onPress={() => removeNode(node.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="trash-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.field}>
        <Input label="Checker Name" value={node.name} onChangeText={(text) => updateNode(node.id, { name: text })} placeholder="e.g. Ask for age" size="sm" />
      </View>
      <NodeConfigForm
        node={node}
        otherNodes={formData.nodes.filter((n) => n.id !== node.id)}
        onChange={(patch) => updateNode(node.id, patch)}
        errors={nodeErrors[node.id] || {}}
      />
    </>
  );

  React.useEffect(() => {
    if (!isWeb || !onPreviewPaneChange) return;
    onPreviewPaneChange(
      <ScrollView contentContainerStyle={styles.webPaneScroll}>
        <Text style={styles.previewTitle}>Flow Preview</Text>
        <Text style={styles.helperText}>How this flow plays out end to end, starting from "{formData.nodes.find((n) => n.id === formData.entryNodeId)?.name || '—'}".</Text>
        {flowPreview.map((step, index) => (
          <View key={step.node.id} style={[styles.previewStep, step.depth === -1 && styles.previewStepUnreachable]}>
            <TouchableOpacity onPress={() => selectNode(step.node.id)} activeOpacity={0.7}>
              <View style={styles.previewStepHeader}>
                <View style={styles.previewStepNumber}><Text style={styles.previewStepNumberText}>{index + 1}</Text></View>
                <Text style={styles.previewStepTitle} numberOfLines={1}>{step.node.name || 'Untitled checker'}</Text>
              </View>
              <Text style={styles.previewStepType}>{outcomeSummaryForNode(step.node, new Map())}</Text>
              {step.depth === -1 && <Text style={styles.previewUnreachableLabel}>Not reachable from the start</Text>}
              {step.edges.map((edge, ei) => (
                <View key={ei} style={styles.previewBranchRow}>
                  <Text style={styles.previewBranchLabel}>{edge.label}</Text>
                  <Text style={styles.previewBranchArrow}>→</Text>
                  <Text style={styles.previewBranchOutcome}>{edge.targetLabel}</Text>
                </View>
              ))}
            </TouchableOpacity>
          </View>
        ))}
        {flowPreview.length === 0 && <Text style={styles.helperText}>Add a starting checker to see the preview.</Text>}
      </ScrollView>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.nodes, formData.entryNodeId, onPreviewPaneChange]);

  React.useEffect(() => {
    if (!isWeb || !onSidebarPaneChange) return;
    onSidebarPaneChange(
      <ScrollView contentContainerStyle={styles.webPaneScroll}>
        {selectedNode ? renderNodeEditor(selectedNode) : renderFlowSettings()}
      </ScrollView>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, selectedNode, nodeErrors, formErrors, onSidebarPaneChange]);

  if (isWeb) {
    return (
      <View style={styles.webCanvasOnly}>
        <FlowCanvas
          nodes={formData.nodes}
          entryNodeId={formData.entryNodeId}
          selectedNodeId={selectedNodeId}
          onSelectNode={selectNode}
          onAddNode={() => { setPendingLink(null); setTypePickerOpen(true); }}
          onAddNodeFrom={addNodeFrom}
          viewCenterRef={viewCenterRef}
          selectedNodeIssues={selectedNodeIssues}
          onEditNode={selectNode}
          onDuplicateNode={(nodeId) => duplicateNodes([nodeId])}
          onDeleteNode={removeNode}
          onClearSelection={() => setSelectedNodeId(null)}
          onRenameEdge={handleRenameEdge}
          onDeleteItems={handleDeleteItems}
          runHighlight={runHighlight}
          onMoveNode={handleMoveNode}
          nodePositions={nodePositions}
          onConnectEdge={handleConnectEdge}
          onDeleteEdge={handleDeleteEdge}
          onSelectionChange={setSelectedNodeIds}
          onMoveNodes={handleMoveNodes}
          selectedNodeIds={selectedNodeIds}
          onDuplicateSelected={() => duplicateNodes(selectedNodeIds)}
          onDeleteSelected={() => removeNodes(selectedNodeIds)}
        />
        {!hideActions && (
          <View style={styles.webCanvasActions}>
            {!!onCancel && <Button title="Cancel" variant="outline" onPress={onCancel} />}
            <Button title={submitLabel} variant="primary" onPress={handleSubmit} loading={loading} />
          </View>
        )}
        <NodeTypePicker visible={typePickerOpen} onClose={() => { setTypePickerOpen(false); setPendingLink(null); }} onSelect={(type) => addNode(type)} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.form}>
        {renderFlowSettings()}
        <Text style={styles.sectionTitle}>Checkers</Text>
        {formData.nodes.map((node) => {
          const isExpanded = expandedNodeIds.has(node.id);
          const isEntry = formData.entryNodeId === node.id;
          const meta = NODE_TYPE_META.find((m) => m.type === node.type);
          return (
            <View key={node.id} style={styles.nodeCard}>
              <TouchableOpacity style={styles.nodeHeader} onPress={() => toggleExpanded(node.id)} activeOpacity={0.7}>
                <Ionicons name={isExpanded ? 'chevron-down' : 'chevron-forward'} size={18} color={colors.textDark} />
                {meta && <AppIcon name={meta.icon} size={18} color={meta.color} />}
                <View style={styles.nodeHeaderContent}>
                  <View style={styles.nodeTitleRow}>
                    <Text style={styles.nodeTitle}>{node.name || 'Untitled checker'}</Text>
                    {isEntry && <View style={styles.entryBadge}><Text style={styles.entryBadgeText}>Start</Text></View>}
                  </View>
                  <Text style={styles.nodeSubtitle}>{meta?.label}</Text>
                </View>
                {formData.nodes.length > 1 && node.type !== 'start' && (
                  <TouchableOpacity onPress={() => removeNode(node.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                    <Ionicons name="trash-outline" size={18} color={colors.error} />
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
              {isExpanded && (
                <View style={styles.nodeBody}>
                  <NodeConfigForm
                    node={node}
                    otherNodes={formData.nodes.filter((n) => n.id !== node.id)}
                    onChange={(patch) => updateNode(node.id, patch)}
                    errors={nodeErrors[node.id] || {}}
                  />
                </View>
              )}
            </View>
          );
        })}
        <TouchableOpacity style={styles.addGroupButton} onPress={() => setTypePickerOpen(true)}>
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={styles.addGroupButtonText}>Add Checker</Text>
        </TouchableOpacity>
        {!hideActions && (
          <View style={styles.actions}>
            {!!onCancel && <View style={styles.cancelButton}><Button title="Cancel" variant="outline" onPress={onCancel} fullWidth /></View>}
            <View style={styles.submitButton}><Button title={submitLabel} variant="primary" onPress={handleSubmit} loading={loading} fullWidth /></View>
          </View>
        )}
      </View>
      <NodeTypePicker visible={typePickerOpen} onClose={() => { setTypePickerOpen(false); setPendingLink(null); }} onSelect={(type) => addNode(type)} />
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  contentContainer: { padding: spacing.lg },
  form: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.lg, ...designTokens.shadows.sm },
  field: { marginBottom: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: spacing.xs },
  helperText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  errorText: { fontSize: typography.fontSize.xs, color: colors.error, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  sectionTitle: { fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold, marginTop: spacing.lg, marginBottom: spacing.xs },
  nodePickList: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  nodePickOption: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, maxWidth: 160 },
  nodePickOptionActive: { borderColor: colors.primary, backgroundColor: `${String(colors.primary)}15` },
  nodePickOptionText: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  nodePickOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  nodeCard: { marginTop: spacing.md, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, overflow: 'hidden' },
  nodeHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, backgroundColor: colors.lightGray },
  nodeHeaderContent: { flex: 1 },
  nodeTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  nodeTitle: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  nodeSubtitle: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  entryBadge: { backgroundColor: `${String(colors.primary)}15`, paddingHorizontal: spacing.xs, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  entryBadgeText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  nodeBody: { padding: spacing.md },
  addGroupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: designTokens.borderRadius.md,
    borderStyle: 'dashed',
  },
  addGroupButtonText: { fontSize: typography.fontSize.base, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
  cancelButton: { flex: 1 },
  submitButton: { flex: 1 },
  webCanvasOnly: { flex: 1, minHeight: 600, padding: spacing.md },
  webCanvasActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  webPaneScroll: { padding: spacing.lg },
  canvasPanelHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, paddingBottom: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  canvasPanelHeaderTitle: { flex: 1 },
  previewTitle: { fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold, marginBottom: spacing.xs / 2 },
  previewStep: { marginTop: spacing.md, padding: spacing.sm, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.surface, borderWidth: 1, borderColor: colors.border },
  previewStepUnreachable: { opacity: 0.6, borderStyle: 'dashed' },
  previewStepHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs / 2 },
  previewStepNumber: { width: 20, height: 20, borderRadius: 10, backgroundColor: `${String(colors.primary)}15`, justifyContent: 'center', alignItems: 'center' },
  previewStepNumberText: { fontSize: 11, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  previewStepTitle: { flex: 1, fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  previewStepType: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginBottom: spacing.xs },
  previewUnreachableLabel: { fontSize: typography.fontSize.xs, color: colors.error, fontFamily: typography.fontFamily.medium, marginBottom: spacing.xs },
  previewBranchRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.xs / 2, marginTop: spacing.xs / 2 },
  previewBranchLabel: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  previewBranchArrow: { fontSize: typography.fontSize.xs, color: colors.textLight },
  previewBranchOutcome: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});
